import React from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/contexts/AuthContext";
import Logo from "./Logo";
import LanguageToggle from "./LanguageToggle";
import ManualButton from "./ManualButton";
import { trackEvent } from "@/lib/track";
import { Button } from "@/components/ui/button";
import {
  LogOut,
  User as UserIcon,
  LayoutDashboard,
  Shield,
  Users as UsersIcon,
} from "lucide-react";

export default function Navbar() {
  const { user, logout } = useAuth();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();

  const onLogout = async () => {
    await logout();
    navigate("/");
  };

  const isLanding = location.pathname === "/";
  const isSales = user && user.role === "sales";

  return (
    <nav
      className={`w-full ${
        isLanding
          ? "absolute z-30"
          : "sticky top-0 z-30 border-b border-white/5 bg-black/60 backdrop-blur-xl"
      }`}
      data-testid="main-navbar"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-10 h-16 flex items-center justify-between gap-2">
        {/* iter96.1 · Click on logo → external redirect to parent brand site.
             Opens in a new tab so we don't lose the user session on onefdp. */}
        <a
          href="https://www.fabricadeprofile.ro"
          target="_blank"
          rel="noopener noreferrer"
          data-testid="nav-home-link"
          className="shrink-0 transition-opacity hover:opacity-90"
          aria-label="FDP 3D — Fabrica de Profile"
          onClick={() => trackEvent("brand.logo.click", { target: "fabricadeprofile.ro" })}
        >
          <Logo showText={false} />
        </a>

        <div className="flex items-center gap-2 sm:gap-3 min-w-0">
          {user ? (
            <>
              {user.role !== "admin" && (
                <Link
                  to="/dashboard"
                  className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition"
                  data-testid="nav-dashboard-link"
                >
                  <LayoutDashboard size={16} strokeWidth={1.5} /> {t("nav.dashboard")}
                </Link>
              )}

              {isSales && (
                <Link
                  to="/sales/all"
                  className="hidden md:inline-flex items-center gap-2 text-sm text-zinc-300 hover:text-white transition"
                  data-testid="nav-sales-all-link"
                >
                  <UsersIcon size={16} strokeWidth={1.5} /> Toate simulările
                </Link>
              )}

              {user.role === "admin" && (
                <Link
                  to="/admin"
                  className="hidden md:inline-flex items-center gap-2 text-sm text-orange-400 hover:text-orange-300 transition"
                  data-testid="nav-admin-link"
                >
                  <Shield size={16} strokeWidth={1.5} /> {t("nav.admin")}
                </Link>
              )}

              <Link
                to="/account"
                className="flex items-center gap-2 text-xs tracking-wide text-zinc-300 hover:text-white px-3 py-1 border border-white/10 hover:border-orange-500/40 rounded-full max-w-[260px] transition"
                title={user.email}
                data-testid="nav-account-link"
              >
                <UserIcon size={12} strokeWidth={1.5} className="text-zinc-500 shrink-0" />
                <span data-testid="navbar-user-name" className="hidden md:inline truncate">
                  {user.name?.trim() || user.email}
                </span>
              </Link>

              <Button
                onClick={onLogout}
                variant="ghost"
                size="sm"
                className="text-zinc-300 hover:text-white hover:bg-white/5"
                data-testid="logout-btn"
              >
                <LogOut size={14} strokeWidth={1.5} className="mr-1.5" />
                {t("nav.logout")}
              </Button>
              <ManualButton />
              <LanguageToggle className="hidden md:inline-flex" />
            </>
          ) : (
            <>
              <Link
                to="/login"
                className="text-sm text-zinc-300 hover:text-white transition whitespace-nowrap"
                data-testid="nav-login-link"
              >
                {t("nav.login")}
              </Link>
              <Link to="/register" data-testid="nav-register-link">
                <Button className="bg-orange-500 hover:bg-orange-600 text-white btn-orange-glow h-9 px-3 text-xs sm:h-10 sm:px-4 sm:text-sm whitespace-nowrap">
                  {t("nav.start_now")}
                </Button>
              </Link>
              <LanguageToggle />
            </>
          )}
        </div>
      </div>
    </nav>
  );
}
